import { ReactElement, useEffect } from 'react';
import { Switch, Route, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useKeychain } from '@treams/auth';
import { Portal } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import AuthorizationContext from 'packages/kakapo-ui-application/contexts/AuthorizationContext';
import publicRoutes from 'packages/kakapo-ui-application/routers/publicRoutes';
import MainRouter, { MainRoutes } from 'packages/kakapo-ui-application/containers/MainRouter';

import FullPageLoadingContainer from '../loading/FullPageLoadingContainer';
import {
	useMixpanelIdentify,
	useMixpanelPeople,
} from 'packages/kakapo-ui-application/hooks/useMixpanel';
import ModalProvider from 'packages/kakapo-ui-kit/components/modals/ModalProvider';
import PopoverProvider from 'packages/kakapo-ui-kit/components/popovers/PopoverProvider';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';

import PrivacyConsent from './PrivacyConsent';
import HelpdeskContainer from 'packages/kakapo-ui-application/common/containers/HelpdeskContainer';

import ModalNavigation from 'navigation/ModalNavigation';
import Satismeter from 'third-party/Satismeter';
import { useWorkspaceLocale } from 'utility/hooks/useWorkspaceLocale';
import PrintConversation from 'conversations/PrintConversation';

function MixpanelIdentity() {
	const authenticatedUser = useAuthenticatedUser();
	const location = useLocation();

	useMixpanelIdentify(authenticatedUser?.id);

	useMixpanelPeople(
		authenticatedUser && {
			$name: `${authenticatedUser.firstName} ${authenticatedUser.lastName}`,
			$email: authenticatedUser.email,
		},
	);

	return null;
}

function Beamer() {
	const viewer = useAuthenticatedUser();
	const workspace = useWorkspace();
	const roles = useViewerRoles();

	useEffect(() => {
		if (roles.loading) {
			return;
		}

		const filter = [workspace.id];

		if (roles.isWorkspaceSuperuser) {
			filter.push('superuser');
		}

		if (roles.isManager) {
			filter.push('manager');
		}

		(window as any).beamer_config = {
			product_id: 'isiHSwoy49709',
			button: false,
			selector: '.beamerButton',
			display: 'right',
			language: 'NL',
			user_id: viewer.id,
			filter: filter.join(';'),
			filter_by_url: true,
		};
		const script = document.createElement('script');
		script.async = true;
		script.src = 'https://app.getbeamer.com/js/beamer-embed.js';
		document.head.appendChild(script);

		return () => {
			document.head.removeChild(script);
		};
	}, [roles.loading]);

	return null;
}

function GetSiteControl() {
	const roles = useViewerRoles();
	const workspace = graphql.useViewerWorkspacePlanQuery();

	useEffect(() => {
		if (roles.loading || !workspace.data) {
			return;
		}

		const script = document.createElement('script');
		script.src = '//l.getsitecontrol.com/l46q3gl7.js';
		script.async = true;
		document.head.appendChild(script);

		const w = window as any;

		w.gsc =
			w.gsc ||
			function () {
				(w.gsc.q = w.gsc.q || []).push(arguments);
			};

		w.gsc('params', {
			'workspace.plan.type': workspace.data.viewer.workspace.plans[0]?.type ?? 'trial',
			'viewer.roles.isSuperuser': roles.isWorkspaceSuperuser.toString(),
		});
	}, [roles.loading, workspace.loading]);

	return null;
}

export default function AppAuthenticated(): ReactElement {
	const { i18n } = useTranslation();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const keychain = useKeychain();
	const workspace = useWorkspace();
	const viewer = graphql.useViewerQuery();

	const availableLanguageQuery = graphql.useSupportedLanguagesInWorkspaceQuery({
		variables: {
			workspaceID: workspace.id,
		},
	});

	const authenticatedUser = viewer.data?.viewer;
	const availableLanguages = availableLanguageQuery.data?.workspace.supportedLanguageIDs ?? [];
	const currentLanguage = viewer.data?.viewer?.locale ?? i18n.language;
	useWorkspaceLocale(availableLanguages, currentLanguage);

	useEffect(() => {
		if (viewer.error) {
			keychain.delete();
		}
	}, [!!viewer.error]);

	if (!authenticatedUser) {
		return <FullPageLoadingContainer />;
	}

	return (
		<AuthorizationContext.Provider
			value={{
				authenticatedUser: authenticatedUser as graphql.User,
			}}
		>
			<Beamer />
			<MixpanelIdentity />
			<GetSiteControl />
			<Portal.Provider fontFamily="Source Sans Pro">
				<ModalProvider>
					<PopoverProvider>
						{authenticatedUser?.privacyConsent?.version !== '1.5' ? (
							<HelpdeskContainer>
								<PrivacyConsent />
							</HelpdeskContainer>
						) : (
							<>
								<Satismeter />
								<Switch>
									{[...publicRoutes]}
									<Route
										path={[
											'/user/:userID/evaluations-v2/:id/print',
											'/v2/conversations/:id/print',
											'/v2/users/:userID/conversations/:id/print',
										]}
										exact
									>
										{({ match }) => <PrintConversation conversationID={match!.params.id!} />}
									</Route>
									<Route>
										<MainRouter />
									</Route>
								</Switch>
								<ModalNavigation url={params.get('modal') ?? ''}>
									<MainRoutes />
								</ModalNavigation>
							</>
						)}
					</PopoverProvider>
				</ModalProvider>
			</Portal.Provider>
		</AuthorizationContext.Provider>
	);
}
